import { getEntitlementDenialMessage } from "../capability/entitlementDenial";

/**
 * 后端错误读取单点（错误链路治理 E6）。
 *
 * 后端统一错误响应体（ErrorResponse）形如：
 * { success, error_code, error_message, details: [{field, message, ...}], request_id, trace_id }
 * 此前 createHttpClient 与各组件各自实现了四套结构（detail/error_code/error_message/details）
 * 的读取逻辑且优先级不一，字段级错误被顶层笼统文案短路——统一收敛到本模块。
 */

interface ErrorBody {
  details?: unknown;
  detail?: unknown;
  error_message?: unknown;
  message?: unknown;
  trace_id?: unknown;
  error_code?: unknown;
}

interface HttpLikeError {
  response?: {
    status?: number;
    headers?: Record<string, unknown>;
    data?: Record<string, unknown>;
  };
  status?: number;
  request?: unknown;
  message?: string;
  config?: { url?: string };
}

function formatErrorItem(item: unknown): string {
  if (typeof item === "string") return item;
  if (!item || typeof item !== "object") return "";

  const data = item as {
    field?: string;
    message?: string;
    msg?: string;
    loc?: unknown[];
  };
  const field =
    data.field || (Array.isArray(data.loc) ? data.loc.join(".") : "");
  const message = data.message || data.msg || "";
  return [field, message].filter(Boolean).join(": ");
}

/**
 * 从后端错误响应体提取最具体的可展示消息。
 *
 * 优先级（E5）：
 * 1. details 数组的字段级错误（"字段: 提示" 逐条拼接）——先于顶层笼统文案；
 * 2. error_message（统一响应体顶层）；
 * 3. detail（FastAPI 原生 string / {message|error} 对象形态）；
 * 4. message。
 */
export function extractErrorBodyMessage(data: unknown): string {
  if (!data || typeof data !== "object") return "";
  const body = data as ErrorBody;

  const details = Array.isArray(body.details)
    ? body.details
    : Array.isArray(body.detail)
      ? body.detail
      : [];
  const fieldMessages = details.map(formatErrorItem).filter(Boolean);
  if (fieldMessages.length > 0) return fieldMessages.join("；");

  if (typeof body.error_message === "string" && body.error_message) {
    return body.error_message;
  }

  const detail = body.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const obj = detail as { message?: unknown; error?: unknown };
    const text = obj.message || obj.error;
    if (text) return String(text);
  }
  if (typeof body.message === "string" && body.message) return body.message;
  return "";
}

/**
 * 从错误响应提取追踪号（报障编号）。
 * 优先响应头 X-Trace-ID / X-Request-ID，退回响应体 trace_id；
 * 与后端 E2 对齐后三者同源，用户凭此号可反查服务端日志。
 */
export function extractReportTraceId(error: unknown): string {
  const value = error as HttpLikeError;
  if (!value?.response) return "";
  const headers = (value.response.headers || {}) as Record<string, string>;
  const fromHeader = headers["x-trace-id"] || headers["x-request-id"] || "";
  if (fromHeader) return String(fromHeader);
  const data = value.response.data as { trace_id?: unknown } | undefined;
  if (data && typeof data.trace_id === "string" && data.trace_id) {
    return data.trace_id;
  }
  return "";
}

/** 报障用的短编号：追踪号前 8 位，避免长串刺眼。 */
export function formatReportTag(traceId: string): string {
  return traceId ? `（报障编号 ${traceId.slice(0, 8)}）` : "";
}

/** 将 Axios/fetch 风格错误转换为可安全展示的统一消息。 */
export function extractApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  // 模块未授权：返回业务名称文案，不暴露能力代码和 HTTP 状态码
  const denial = getEntitlementDenialMessage(error);
  if (denial) return denial;

  const value = error as HttpLikeError;
  const status = value.response?.status ?? value.status;

  let message = extractErrorBodyMessage(value.response?.data);
  if (!message && value.message) message = String(value.message);
  if (!message) message = fallback;

  if (!value.response && value.request) return `网络连接失败：${message}`;
  return typeof status === "number" ? `HTTP ${status}：${message}` : message;
}
